import { useCallback, useEffect, useRef, useState } from 'react'

const DESKTOP = '(min-width: 1024px)'
/** Track's left padding on mobile; slides snap to this edge so the next one peeks in. */
const MOBILE_INSET = 10

/**
 * Scroll-snap carousel state. Mobile: slides align to the left edge.
 * Desktop: the active slide is centred with one neighbour on each side.
 */
export function useLookInsideCarousel(count: number) {
  const trackRef = useRef<HTMLDivElement>(null)
  const slideRefs = useRef<(HTMLDivElement | null)[]>([])
  const [active, setActive] = useState(0)

  const goTo = useCallback((index: number, instant = false) => {
    const track = trackRef.current
    const slide = slideRefs.current[index]
    if (!track || !slide) return
    const desktop = window.matchMedia(DESKTOP).matches
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const left = desktop
      ? slide.offsetLeft - (track.clientWidth - slide.clientWidth) / 2
      : slide.offsetLeft - MOBILE_INSET
    track.scrollTo({ left, behavior: instant || reduce ? 'auto' : 'smooth' })
  }, [])

  const next = useCallback(() => goTo(Math.min(active + 1, count - 1)), [active, count, goTo])
  const prev = useCallback(() => goTo(Math.max(active - 1, 0)), [active, goTo])

  useEffect(() => {
    const track = trackRef.current
    if (!track) return
    let frame = 0

    const measure = () => {
      const desktop = window.matchMedia(DESKTOP).matches
      const anchor = desktop
        ? track.scrollLeft + track.clientWidth / 2
        : track.scrollLeft + MOBILE_INSET
      let closest = 0
      let best = Infinity
      slideRefs.current.forEach((slide, i) => {
        if (!slide) return
        const point = desktop ? slide.offsetLeft + slide.clientWidth / 2 : slide.offsetLeft
        const d = Math.abs(point - anchor)
        if (d < best) {
          best = d
          closest = i
        }
      })
      // At the very end of the track on mobile, the last slide can't reach the left edge.
      if (!desktop && track.scrollLeft + track.clientWidth >= track.scrollWidth - 2) {
        closest = count - 1
      }
      setActive(closest)
    }

    const onScroll = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(measure)
    }

    track.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    // On desktop, open on the second page so three full pages are in view.
    if (window.matchMedia(DESKTOP).matches && count > 2) goTo(1, true)
    measure()

    return () => {
      cancelAnimationFrame(frame)
      track.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [count, goTo])

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault()
        next()
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        prev()
      }
    },
    [next, prev],
  )

  return { trackRef, slideRefs, active, goTo, next, prev, onKeyDown }
}
